import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header, Footer } from "../components/Chrome";
import { useSettings } from "../lib/settings";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

const BANTA_HERO = "https://customer-assets-agu9un31.emergentagent.net/job_snack-supply-hub/artifacts/zzsxp7kf_WhatsApp%20Image%202026-07-23%20at%204.52.51%20PM.jpeg";

// Static flavour list — Banta is a curated product family, managed as normal Products in the catalogue.
const FLAVOURS = [
  { name: "Nimbu Pani", en: "Citron Épicé", notes: "Zesty · Refreshing · Classic", color: "#8BB93D", accent: "#5c7d22" },
  { name: "Masala Pineapple", en: "Ananas Épicé", notes: "Sweet · Spicy · Tropical", color: "#F08A1D", accent: "#c56a08" },
  { name: "Blueberry", en: "Bleuet", notes: "Bold · Fruity · Refreshing", color: "#2E7CC2", accent: "#1f5c96" },
  { name: "Jeera Shikanji", en: "Citron au Cumin", notes: "Tangy · Spiced · Digestive", color: "#9A5B33", accent: "#6f3f22" },
];

const FEATURES = [
  ["Premium Extra Codd Soda", "The signature marble-stopper glass bottle — opened with a satisfying pop."],
  ["Made With Quality Ingredients", "Authentic recipes, real flavour, no shortcuts."],
  ["Glass Bottle, Premium Feel", "A nostalgic format that elevates any shelf or fridge."],
  ["Authentic Indian Nostalgia", "Born in India. Made for Canada."],
];

function EnquirySection() {
  const { settings } = useSettings();
  const [f, setF] = useState({ business_name: "", contact_name: "", phone: "", email: "", city: "", products_of_interest: "Banta (Codd-Neck) — all flavours", estimated_quantities: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", f);
      setSent(data.inquiry_no);
      toast.success(`Enquiry #${data.inquiry_no} sent. We'll be in touch within 1 business day.`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!settings?.enquiry_form_enabled) {
    return (
      <div className="border border-white/20 p-8 text-center">
        <Link to="/contact" className="btn-primary" style={{ background: "#fff", borderColor: "#fff", color: "#050505" }}>Contact us about Banta</Link>
      </div>
    );
  }
  if (sent) {
    return (
      <div className="border border-white/20 p-8 md:p-12 text-center" data-testid="banta-enquiry-success">
        <div className="overline text-white/60 mb-3">Received · Ref #{sent}</div>
        <h3 className="font-display text-4xl md:text-5xl uppercase">Thanks — we've got it.</h3>
        <p className="mt-4 text-white/70">Your reference number is #{sent}. Our team will reply within one business day with wholesale pricing and availability.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="border border-white/20 p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="banta-enquiry-form">
      <input required placeholder="Business name" value={f.business_name} onChange={e => setF({ ...f, business_name: e.target.value })} className="field-dark" data-testid="banta-business" />
      <input required placeholder="Contact name" value={f.contact_name} onChange={e => setF({ ...f, contact_name: e.target.value })} className="field-dark" data-testid="banta-contact" />
      <input required placeholder="Phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="field-dark" data-testid="banta-phone" />
      <input required type="email" placeholder="Email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className="field-dark" data-testid="banta-email" />
      <input placeholder="Delivery city" value={f.city} onChange={e => setF({ ...f, city: e.target.value })} className="field-dark" data-testid="banta-city" />
      <input placeholder="Estimated quantity (e.g. 20 cases)" value={f.estimated_quantities} onChange={e => setF({ ...f, estimated_quantities: e.target.value })} className="field-dark" data-testid="banta-qty" />
      <textarea rows={3} placeholder="Which flavours are you interested in?" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="field-dark md:col-span-2" data-testid="banta-notes" />
      <button disabled={busy} className="btn-primary md:col-span-2" style={{ background: "#E63946", borderColor: "#E63946" }} data-testid="banta-submit">
        {busy ? "Sending…" : "Request Banta wholesale pricing →"}
      </button>
    </form>
  );
}

export default function Banta() {
  return (
    <div className="min-h-screen bg-ink text-paper">
      <Header dark />

      {/* Hero */}
      <section className="relative pt-24 md:pt-28 overflow-hidden" data-testid="banta-hero">
        <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16,1,0.3,1] }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2 h-2 bg-soda animate-pulse" />
              <span className="overline text-white/60">Premium Indian Beverage · Imported Reserve</span>
            </div>
            <h1 className="font-display text-[15vw] md:text-[12vw] lg:text-[10vw] leading-[0.82] uppercase tracking-tighter">
              BANTA<br/><em className="font-editorial italic tracking-normal text-soda normal-case">Apna Desi Drink.</em>
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
              Nostalgic flavours, modern experience. A perfect blend of authentic Indian taste and premium quality — the marble-stopper Codd-neck soda, <strong>born in India, made for Canada.</strong>
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2, ease: [0.76,0,0.24,1] }}
            className="mt-12 border border-white/15 overflow-hidden hard-shadow-inv"
          >
            <img src={BANTA_HERO} alt="Banta premium Codd soda range — Nimbu Pani, Masala Pineapple, Blueberry, Jeera Shikanji" className="w-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-white/10" data-testid="banta-features">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map(([t, d], i) => (
            <motion.div key={t}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="border border-white/15 p-6 hover:border-soda transition-colors duration-300"
            >
              <div className="font-display text-5xl text-soda mb-4">0{i+1}</div>
              <h3 className="font-bold uppercase tracking-wide text-sm mb-2">{t}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Flavours */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 border-t border-white/10" data-testid="banta-flavours">
        <div className="overline text-white/50 mb-6">The Flavours</div>
        <h2 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tighter mb-14">Four ways<br/>to pop the cap.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {FLAVOURS.map((fl, i) => (
            <motion.div key={fl.name}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16,1,0.3,1] }}
              className="relative border border-white/15 overflow-hidden group"
              data-testid={`banta-flavour-${i}`}
            >
              <div className="h-40 flex items-end p-5 transition-all duration-500" style={{ background: `linear-gradient(160deg, ${fl.color}, ${fl.accent})` }}>
                <span className="font-display text-3xl uppercase text-white drop-shadow">{fl.name}</span>
              </div>
              <div className="p-5">
                <div className="font-editorial italic text-lg text-white/80">{fl.en}</div>
                <div className="overline text-white/50 mt-3">{fl.notes}</div>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3 text-xs uppercase tracking-widest text-white/50">
          <span className="chip border-white/30">Made in India</span>
          <span className="chip border-white/30">Imported to Canada</span>
          <span className="chip border-white/30">Best served chilled</span>
          <span className="chip border-white/30">Loved by all ages</span>
        </div>
      </section>

      {/* Wholesale enquiry */}
      <section className="relative border-t border-white/10 overflow-hidden" data-testid="banta-enquiry-section">
        <div className="absolute inset-0 spot-radial" />
        <div className="relative px-6 md:px-12 lg:px-16 py-20 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="overline text-white/50 mb-6">Wholesale enquiry</div>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.9] tracking-tighter">Stock Banta<br/>in your store.</h2>
              <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
                Give your customers something no one else on the block has. Send us your details and we'll reply with wholesale pricing and availability within one business day.
              </p>
            </div>
            <div className="lg:col-span-7">
              <EnquirySection />
            </div>
          </div>
        </div>
      </section>

      <div className="bg-paper text-ink"><Footer /></div>
    </div>
  );
}
